import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { api } from "@/utils/axiosInterceptor";
import type { downloadedEInvoice } from "@/types/downloadedEInvoice";
import type { LhdnSearchDocumentsParams } from "@/types/lhdnSearchDocumentsParams";

export type RecentApiResponse = {
  results: downloadedEInvoice[];
};

export type InvoiceType = "Sales" | "Purchases";

export const useDownloadEInvoices = (invoiceType: InvoiceType) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["downloaded-documents", invoiceType],
    queryFn: async () =>
      (await api.get<RecentApiResponse>(`/downloaded-documents/${invoiceType}`)).data,
    enabled: true,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const backfillDownloads = async (params: { startDate: string; endDate?: string }) => {
    const res = await api.get<{ message: string; startDate: string; endDate: string }>(
      `/downloaded-documents/backfill/${invoiceType}`,
      { params }
    );
    return res.data;
  };

  const refreshDownloads = async (params?: LhdnSearchDocumentsParams) => {
    const res = await api.get<RecentApiResponse>(
      `/downloaded-documents/refresh/${invoiceType}`,
      { params }
    );

    queryClient.setQueryData<RecentApiResponse>(
      ["downloaded-documents", invoiceType],
      res.data
    );

    return res.data;
  };

  const updateGlCode = useMutation({
    mutationFn: async (payload: { uuid: string; debitGlCode: string | null, creditGlCode: string | null}) => {
      const res = await api.post("/downloaded-documents/update-gl-code", payload);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.setQueryData<RecentApiResponse>(
        ["downloaded-documents", invoiceType],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            results: old.results.map((r) =>
              r.uuid === variables.uuid ? { ...r, debitGlCode: variables.debitGlCode, creditGlCode: variables.creditGlCode } : r
            ),
          };
        }
      );
    },
  });

  return {
    data: query.data,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    refreshDownloads,
    backfillDownloads,
    updateGlCode
  };
};
